import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../ui/components/Dialog";
import { useAppSelector } from "../../../store/hooks";
import { checkRole } from "../../../components/recipe_page/recipe_page";
import RaceTimeline from "./shared/RaceTimeline";
import {
  useAuditRaceMutation,
  useGetRaceAuditQuery,
} from "../api/artOfTheDayApi";
import type { ArtInfo, RaceAuditResult, RaceAuditState } from "../api/types";

/**
 * Race Auditor entry point — rendered under the AOTD stats once a race
 * happened. A participant in today's race (or an admin) can DISPUTE the result;
 * the backend runs an unbiased gpt-5.5 audit and we render the verdict,
 * confidence, per-participant analysis, and the race timeline in a modal.
 * The audit is cached per day, so anyone can view a completed audit; only
 * participants/admins can trigger one, and only admins can force a re-run.
 */

const VERDICT_META: Record<
  string,
  { label: string; color: string; bg: string; icon: string }
> = {
  consistent: { label: "Result is correct", color: "#15803d", bg: "#dcfce7", icon: "✓" },
  inconsistent: { label: "Problem found", color: "#b91c1c", bg: "#fee2e2", icon: "✗" },
  inconclusive: { label: "Inconclusive", color: "#b45309", bg: "#fef3c7", icon: "?" },
};

function isDone(a: RaceAuditState | null | undefined): a is RaceAuditResult {
  return !!a && a.status === "done";
}

const ConfidenceMeter: React.FC<{ value: number; color: string }> = ({ value, color }) => (
  <div style={{ marginTop: 6 }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: 11,
        color: "#6b7280",
        marginBottom: 3,
      }}
    >
      <span>confidence</span>
      <span style={{ fontWeight: 600, color }}>{value}%</span>
    </div>
    <div style={{ height: 8, background: "#e5e7eb", borderRadius: 9999, overflow: "hidden" }}>
      <div
        style={{
          height: "100%",
          width: `${Math.max(0, Math.min(100, value))}%`,
          background: color,
          borderRadius: 9999,
          transition: "width .4s ease",
        }}
      />
    </div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: 16 }}>
    <h4 style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "#6b7280", marginBottom: 6 }}>
      {title}
    </h4>
    {children}
  </div>
);

const Bullets: React.FC<{ items: string[]; tone?: "default" | "warn" }> = ({ items, tone }) => (
  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5, color: tone === "warn" ? "#b91c1c" : "#374151" }}>
    {items.map((it, i) => (
      <li key={i} style={{ marginBottom: 3 }}>{it}</li>
    ))}
  </ul>
);

const AuditBody: React.FC<{ result: RaceAuditResult; race?: ArtInfo["race"]; generationTimeMs?: number }> = ({
  result,
  race,
  generationTimeMs,
}) => {
  const meta = VERDICT_META[result.verdict] ?? VERDICT_META.inconclusive;
  return (
    <div>
      {/* verdict badge + confidence */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 14px",
          borderRadius: 10,
          background: meta.bg,
          marginTop: 4,
        }}
      >
        <span style={{ fontSize: 22, fontWeight: 800, color: meta.color }}>{meta.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: meta.color }}>{meta.label}</div>
          <ConfidenceMeter value={result.confidence} color={meta.color} />
        </div>
      </div>

      {result.summary ? (
        <p style={{ fontSize: 14, lineHeight: 1.55, color: "#374151", marginTop: 12 }}>{result.summary}</p>
      ) : null}

      {result.degraded ? (
        <p style={{ fontSize: 12, color: "#b45309", marginTop: 8 }}>
          ⚠ The auditor couldn't return a fully structured result; showing the deterministic cross-check.
        </p>
      ) : null}

      {race ? (
        <Section title="Race timeline">
          <RaceTimeline race={race} generationTimeMs={generationTimeMs} />
        </Section>
      ) : null}

      {result.reasons.length > 0 && (
        <Section title="Why">
          <Bullets items={result.reasons} />
        </Section>
      )}

      {result.anomalies.length > 0 && (
        <Section title="Anomalies">
          <Bullets items={result.anomalies} tone="warn" />
        </Section>
      )}

      {result.perParticipant.length > 0 && (
        <Section title="Per participant">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {result.perParticipant.map((p, i) => (
              <div
                key={i}
                style={{
                  border: "1px solid #e5e7eb",
                  borderLeft: `3px solid ${p.flaggedUnfair ? "#b91c1c" : "#d1d5db"}`,
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>
                    {p.username}{" "}
                    <span style={{ fontWeight: 400, color: "#6b7280" }}>· {p.outcome}</span>
                  </span>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    {p.tapOffsetMs != null ? `+${p.tapOffsetMs}ms` : ""}
                  </span>
                </div>
                {p.assessment ? (
                  <div style={{ color: p.flaggedUnfair ? "#b91c1c" : "#4b5563", marginTop: 2 }}>{p.assessment}</div>
                ) : null}
              </div>
            ))}
          </div>
        </Section>
      )}

      {result.processNotes.length > 0 && (
        <Section title="Process notes">
          <Bullets items={result.processNotes} />
        </Section>
      )}

      {/* deterministic cross-check (small print) */}
      <Section title="Deterministic cross-check">
        <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
          winner: <strong>{result.independentVerdict.winnerName ?? "—"}</strong>
          {" · "}losers: {result.independentVerdict.losers.join(", ") || "—"}
          {" · "}contested: {String(result.independentVerdict.contested)}
          {result.independentVerdict.anomalies.length > 0 && (
            <>
              {" · "}anomalies: {result.independentVerdict.anomalies.join("; ")}
            </>
          )}
        </div>
      </Section>

      <div style={{ marginTop: 14, fontSize: 11, color: "#9ca3af" }}>
        {result.modelId} · {result.auditorVersion} ·{" "}
        {new Date(result.generatedAt).toLocaleString()}
        {result.generatedByUsername ? ` · disputed by ${result.generatedByUsername}` : ""}
      </div>
    </div>
  );
};

export interface RaceAuditPanelProps {
  artInfo: ArtInfo;
}

const RaceAuditPanel: React.FC<RaceAuditPanelProps> = ({ artInfo }) => {
  const role = useAppSelector((s) => s.auth.role);
  const isAdmin = checkRole(role, "admin");
  const [open, setOpen] = useState(false);

  // Single subscription; poll only while an audit is running (someone mid-dispute).
  const [running, setRunning] = useState(false);
  const cachedAudit = useGetRaceAuditQuery(undefined, {
    pollingInterval: running ? 4000 : 0,
  });
  const auditState = cachedAudit.data?.audit ?? null;
  const isRunningRemote = auditState?.status === "running";
  React.useEffect(() => {
    setRunning(isRunningRemote);
  }, [isRunningRemote]);

  const [auditRace, auditReq] = useAuditRaceMutation();
  const canDispute = !!cachedAudit.data?.canDispute;

  const result = useMemo(() => (isDone(auditState) ? auditState : null), [auditState]);

  const trigger = async (refresh: boolean) => {
    setOpen(true);
    try {
      await auditRace(refresh ? { refresh: true } : undefined).unwrap();
    } catch {
      /* error surfaced via cache/req state */
    }
  };

  // Nothing to show: no audit yet and the viewer can't dispute.
  if (!result && !canDispute && !isRunningRemote) return null;

  const busy = auditReq.isLoading || isRunningRemote;

  return (
    <div
      style={{
        marginTop: 16,
        paddingTop: 12,
        borderTop: "1px dashed #e5e7eb",
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 13, color: "#6b7280" }}>⚖️ Race fairness</span>

      {result ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={pillStyle(result.verdict)}
        >
          {(VERDICT_META[result.verdict] ?? VERDICT_META.inconclusive).icon} View audit ·{" "}
          {result.confidence}%
        </button>
      ) : null}

      {canDispute && !result && (
        <button
          type="button"
          onClick={() => trigger(false)}
          disabled={busy}
          style={disputeBtnStyle(busy)}
        >
          {busy ? "Auditing…" : "Dispute this race"}
        </button>
      )}

      {canDispute && isAdmin && result && (
        <button
          type="button"
          onClick={() => trigger(true)}
          disabled={busy}
          style={ghostBtnStyle(busy)}
        >
          {busy ? "Re-running…" : "Re-run (admin)"}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Race Auditor</DialogTitle>
          </DialogHeader>
          <div style={{ marginTop: 4 }}>
            {busy && !result ? (
              <div style={{ padding: "28px 0", textAlign: "center", color: "#6b7280" }}>
                <div className="spinner" style={spinnerStyle} />
                <div style={{ marginTop: 12, fontSize: 14 }}>
                  Auditing the race against the contract…
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: "#9ca3af" }}>
                  An unbiased model is re-deriving the result. This can take ~10–30s.
                </div>
              </div>
            ) : result ? (
              <AuditBody
                result={result}
                race={artInfo.race}
                generationTimeMs={artInfo.generationTime}
              />
            ) : auditState?.status === "error" ? (
              <div style={{ padding: 16, color: "#b91c1c", fontSize: 14 }}>
                The audit failed: {auditState.error}. Please try again.
              </div>
            ) : (
              <div style={{ padding: 16, color: "#6b7280", fontSize: 14 }}>
                No audit yet.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <style>{KEYFRAMES}</style>
    </div>
  );
};

const KEYFRAMES = `@keyframes ra-spin { to { transform: rotate(360deg); } }`;
const spinnerStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  border: "3px solid #e5e7eb",
  borderTopColor: "#6b7280",
  borderRadius: "50%",
  margin: "0 auto",
  animation: "ra-spin .8s linear infinite",
};

function pillStyle(verdict: string): React.CSSProperties {
  const m = VERDICT_META[verdict] ?? VERDICT_META.inconclusive;
  return {
    fontSize: 12,
    fontWeight: 600,
    color: m.color,
    background: m.bg,
    border: "none",
    borderRadius: 9999,
    padding: "4px 10px",
    cursor: "pointer",
  };
}
function disputeBtnStyle(busy: boolean): React.CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    background: busy ? "#9ca3af" : "#111827",
    border: "none",
    borderRadius: 9999,
    padding: "5px 12px",
    cursor: busy ? "default" : "pointer",
  };
}
function ghostBtnStyle(busy: boolean): React.CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    background: "transparent",
    border: "1px solid #d1d5db",
    borderRadius: 9999,
    padding: "4px 10px",
    cursor: busy ? "default" : "pointer",
  };
}

export default RaceAuditPanel;
